import { apiService } from '../services/apiService';

// YYYY-MM-DD
const getToday = (): string => new Date().toISOString().split('T')[0];

// ISO full timestamp (e.g., 2025-06-20T10:00:00.000Z)
const getNowISO = (): string => new Date().toISOString();

//------------------------------------------------------ Day Parts ------------------------------------------------------
export const getDayParts = async (): Promise<any> => {
  // Fetch all day parts without pagination; filter by organization on client
  const query = `day_parts?page=1&itemsPerPage=&pagination=false`;
  const data = apiService.responseHandler(await apiService.get(query));
  return data;
};

//------------------------------------------------------ Workplace Daily Summary ------------------------------------------------------
export interface GetWorkplaceDailySummaryParams {
  workplaceId: number;
  date: string;
  page?: number;
}

export interface WorkplaceDailySummary {
  id: number;
  workplace: string;
  author: {
    id: number;
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
  };
  date: string;
  lunchTraffic: number;
  dinnerTraffic: number;
  dailyTraffic: number;
  trafficChangePercent: number | null;
  dailySale: number;
  lunchSale: number;
  dinnerSale: number;
  salesChangePercent: number | null;
}

export const getWorkplaceDailySummary = async ({
  workplaceId,
  date,
  page = 1,
}: GetWorkplaceDailySummaryParams): Promise<any> => {
  const query = `workplace_daily_summaries?page=${page}&date=${date}&workplace=${workplaceId}`;
  const data = apiService.responseHandler(await apiService.get(query));
  return data;
};

//--------------------------------------------------- POST Workplace Daily Summary ---------------------------------------------------
export interface PostWorkplaceDailySummaryParams {
  workplaceId: number;
  date: string;
  authorId?: number; // Optional author ID, defaults to 1 if not provided
  // Optional dynamic maps keyed by day-part titles (e.g., breakfast/lunch/dinner)
  salesParts?: Record<string, number>;
  trafficParts?: Record<string, number>;
}

export const postWorkplaceDailySummary = async ({
  workplaceId,
  date,
  authorId = 4, // Default to user ID 4 if not provided
  salesParts,
  trafficParts,
}: PostWorkplaceDailySummaryParams): Promise<any> => {
  // Always use today's date if none provided
  const currentDate = date || getToday();
  
  // Build dynamic maps if provided, normalizing keys to lowercase without spaces
  const normalizeKey = (key: string) => key?.toString()?.trim().toLowerCase().replace(/\s+/g, ' ') || '';

  const normalizedSales = salesParts
    ? Object.fromEntries(
        Object.entries(salesParts).map(([k, v]) => [normalizeKey(k), Number(v) || 0])
      )
    : undefined;
  const normalizedTraffic = trafficParts
    ? Object.fromEntries(
        Object.entries(trafficParts).map(([k, v]) => [normalizeKey(k), Number(v) || 0])
      )
    : undefined;

  const payload: any = {
    workplace: `api/workplaces/${workplaceId}`,
    author: `api/users/${authorId}`,
    date: new Date(currentDate).toISOString(),
  };

  // If dynamic maps exist, include them under sales/traffic as requested
  if (normalizedSales || normalizedTraffic) {
    if (normalizedSales) payload.sales = normalizedSales;
    if (normalizedTraffic) payload.traffic = normalizedTraffic;
  }

  const response = await apiService.post('workplace_daily_summaries', payload);
  return apiService.responseHandler(response);
};

//--------------------------------------------------- POST Workplace Daily Summary ENDS HERE ------------------------------------------------------

//------------------------------------------------------ Sales ------------------------------------------------------
export interface GetSalesParams {
	workplaceId: number;
	orderBy?: string;
	direction?: 'ASC' | 'DESC';
	limit?: number;
	page?: number;
  date?: string;
}

const useDummyData = true; // Toggle this flag to switch between dummy and real API

// Dummy data
const dummySalesData = [
  {
    id: 1,
    workplaceId: 1, // Changed from 123 to 1
    date: getToday(),
    daily: { value: 10000, change: 1.2 },
    lunch: { value: 4000, isManual: true },
    dinner: { value: 6000, isManual: true },
    wtd: { value: 23500, change: 1.5 },
    ptd: { value: 87976, change: -0.4 },
    created_at: getNowISO(),
  },
];

export const getSales = async ({
  workplaceId,
  orderBy = 'id',
  direction = 'DESC',
  limit = 500,
  page = 1,
  date,
}: GetSalesParams): Promise<any> => {
  if (useDummyData) {
    let filteredItems = Array.isArray(dummySalesData) ? dummySalesData : [];

    if (date) {
      filteredItems = filteredItems.filter(item => item?.date === date);
    }

    return filteredItems;
  }

  const query = `todos/workplace?workplace=${workplaceId}&orderBy=${orderBy}&direction=${direction}&limit=${limit}&page=${page}&date=${date}`;
  const data = apiService.responseHandler(await apiService.get(query));
  return data;
};

export const getSalesById = async (id: number): Promise<any> => {
  if (useDummyData) {
    return dummySalesData.find(item => item.id === id);
  }

  const response = await apiService.get(`todos/${id}`);
  return apiService.responseHandler(response);
};
  //--------------------------------------------------- POST Sales ---------------------------------------------------
  export const postSales = async ({
    workplaceId,
    date,
    dailySales,
    dailyChange,
    lunchSales,
    dinnerSales,
    wtdSales,
    wtdChange,
    ptdSales,
    ptdChange,
  }: {
    workplaceId: number;
    date: string;
    dailySales: number;
    dailyChange: number;
    lunchSales: number;
    dinnerSales: number;
    wtdSales: number;
    wtdChange: number;
    ptdSales: number;
    ptdChange: number;
  }): Promise<any> => {
    if (useDummyData) {
      const newRecord = {
        id: dummySalesData.length + 1,
        workplaceId,
        date,
        daily: { value: dailySales, change: dailyChange },
        lunch: { value: lunchSales, isManual: true },
        dinner: { value: dinnerSales, isManual: true },
        wtd: { value: wtdSales, change: wtdChange },
        ptd: { value: ptdSales, change: ptdChange },
        created_at: new Date().toISOString(),
      };
      dummySalesData.push(newRecord);
      return newRecord;
    }

    // Real API call
    const payload = {
      workplaceId,
      date,
      dailySales,
      dailyChange,
      lunchSales,
      dinnerSales,
      wtdSales,
      wtdChange,
      ptdSales,
      ptdChange,
    };

    const response = await apiService.post('sales', payload);
    return apiService.responseHandler(response);
  };
  //---------------------------------------------- POST Sales ENDS HERE ----------------------------------------------

  //--------------------------------------------------- POST Sales Notes ---------------------------------------------------
  export const postSalesNote = async ({
    note,
    workplaceId = 1, // Static workplace ID set to 1
  }: {
    note: string;
    workplaceId?: number;
  }): Promise<any> => {
    // Remove HTML tags from the note content
    const cleanNote = note.replace(/<[^>]*>/g, '');
    
    const payload = {
      note: cleanNote,
      workplace: `api/workplaces/${workplaceId}`,
    };

    const response = await apiService.post('sales_notes', payload);
    return apiService.responseHandler(response);
  };
  //---------------------------------------------- POST Sales Notes ENDS HERE ----------------------------------------------

//------------------------------------------------ Sales ENDS HERE --------------------------------------------------

//------------------------------------------------------ Traffic ------------------------------------------------------
const useDummyTrafficData = true;

const dummyTrafficData = {
  items: [
    {
      id: 1,
      workplaceId: 1, // Changed from 123 to 1
      date: getToday(),
      daily: { value: 1237, change: 0.8 },
      lunch: { value: 497, isManual: true },
      dinner: { value: 740, isManual: true },
      wtd: { value: 4817, change: 0.09 },
      ptd: { value: 16294, change: -0.2 },
      peakHour: '7:00 PM - 9:00 PM',
      averageDwellTime: '42 mins',
      created_at: getNowISO(),
    },
  ],
  totalCount: 1,
};

export const getTraffic = async ({
  workplaceId,
  date, 
}: {
  workplaceId: number;
  date?: string;
}): Promise<any> => {
  if (useDummyTrafficData) {
    let items = dummyTrafficData.items;
    if (date) {
      items = items.filter(item => item?.date === date);
    }
    return {
      items,
      totalCount: items.length,
    };
  }

  const query = `traffic/workplace?workplace=${workplaceId}&date=${date}`;
  const { ...data } = apiService.responseHandler(await apiService.get(query));
  return data;
};

  //--------------------------------------------------- POST Traffic ---------------------------------------------------
  export const postTraffic = async ({
    workplaceId,
    date,
    daily,
    lunch,
    dinner,
    wtd,
    ptd,
    peakHour,
    averageDwellTime,
  }: {
    workplaceId: number;
    date: string;
    daily: { value: number; change: number };
    lunch: { value: number; isManual: boolean };
    dinner: { value: number; isManual: boolean };
    wtd: { value: number; change: number };
    ptd: { value: number; change: number };
    peakHour: string;
    averageDwellTime: string;
  }): Promise<any> => {
    if (useDummyTrafficData) {
      const newTraffic = {
        id: dummyTrafficData.items.length + 1,
        workplaceId,
        date,
        daily,
        lunch,
        dinner,
        wtd,
        ptd,
        peakHour,
        averageDwellTime,
        created_at: new Date().toISOString(),
      };
      dummyTrafficData.items.push(newTraffic);
      return newTraffic;
    }

    // Real API call
    const payload = {
      workplaceId,
      date,
      daily,
      lunch,
      dinner,
      wtd,
      ptd,
      peakHour,
      averageDwellTime,
    };

    const response = await apiService.post('traffic', payload);
    return apiService.responseHandler(response);
  };
  //---------------------------------------------- POST Traffic ENDS HERE ----------------------------------------------
//-------------------------------------------------- Traffic ENDS HERE  --------------------------------------------------

//-------------------------------------------------------- Events --------------------------------------------------------
const useDummyEventsData = true;

const dummyEventsData = {
  items: [
    {
      id: 1,
      workplaceId: 1, // Changed from 123 to 1
      date: getToday(),
      eventName: 'Manager Visit',
      eventType: 'Internal Audit',
      startTime: '3:00 PM',
      endTime: '4:30 PM',
    },
    {
      id: 2,
      workplaceId: 1, // Changed from 123 to 1
      date: getToday(),
      eventName: 'Happy Hour Promo',
      eventType: 'Promotion',
      startTime: '6:00 PM',
      endTime: '8:00 PM',
    },
  ],
  totalCount: 2,
};

export const getEvents = async ({
  workplaceId,
  date,
}: {
  workplaceId: number;
  date?: string;
}): Promise<any> => {
  if (useDummyEventsData) {
    let items = dummyEventsData.items;
    if (date) {
      items = items.filter(item => item.date === date);
    }
    return {
      items,
      totalCount: items.length,
    };
  }

  const query = `events/workplace?workplace=${workplaceId}&date=${date}`;
  const { ...data } = apiService.responseHandler(await apiService.get(query));
  return data;
};
//--------------------------------------------------- Events ENDS HERE ---------------------------------------------------

//-------------------------------------------------- Checklist Completed -------------------------------------------------
const useDummyChecklistData = false;

const dummyChecklistData = {
  items: [
    {
      id: 1,
      workplaceId: 1, // Changed from 123 to 1
      date: getToday(),
      shift: 'Morning',
      completedBy: 'John Doe',
      totalItems: 12,
      completedItems: 12,
      section: 'Safety',
      duty: 'Fire Safety Checklist',
      instructions: 'Ensure all fire exits are clear',
      answerType: 'True/False',
      correctAnswer: 'True',
      comment: 'All checks completed',
      submittedAnswer: 'True',
      picLink: '',
      time: "06/19/2025 04:24 PM",
      isEditing: false,
      isSaved: true,
      isSelected: false,
      isValid: {
        section: true,
        duty: true,
        instructions: true,
        answerType: true,
        correctAnswer: true,
        comment: true,
        picLink: true,
      },
      errors: {},
    },
    {
      id: 2,
      workplaceId: 1, // Changed from 123 to 1
      date: getToday(),
      shift: 'Evening',
      completedBy: 'Jane Smith',
      totalItems: 12,
      completedItems: 10,
      section: 'Maintenance',
      duty: 'Equipment Check',
      instructions: 'Verify equipment functionality',
      answerType: 'True/False',
      correctAnswer: 'True',
      comment: 'Equipment functional',
      submittedAnswer: 'True',
      picLink: '',
      time: "06/19/2025 04:24 PM",
      isEditing: false,
      isSaved: true,
      isSelected: false,
      isValid: {
        section: true,
        duty: true,
        instructions: true,
        answerType: true,
        correctAnswer: true,
        comment: true,
        picLink: true,
      },
      errors: {},
    },
    {
      id: 3,
      workplaceId: 1, // Changed from 123 to 1
      date: getToday(),
      shift: 'Evening',
      completedBy: 'Jane Smith',
      totalItems: 12,
      completedItems: 10,
      section: 'Operations',
      duty: 'Daily Operations',
      instructions: 'Complete daily tasks',
      answerType: 'True/False',
      correctAnswer: 'True',
      comment: 'No issues',
      submittedAnswer: 'True',
      time: "06/19/2025 04:24 PM",
      picLink: '',
      isEditing: false,
      isSaved: true,
      isSelected: false,
      isValid: {
        section: true,
        duty: true,
        instructions: true,
        answerType: true,
        correctAnswer: true,
        comment: true,
        picLink: true,
      },
      errors: {},
    },
  ],
  totalCount: 2,
};

export const getChecklistCompleted = async ({
  workplaceId,
  date,
}: {
  workplaceId: number;
  date?: string;
}): Promise<any> => {
  if (useDummyChecklistData) {
    let items = dummyChecklistData.items;
    if (date) {
      items = items.filter(item => item.date === date);
    }
    return {
      items,
      totalCount: items.length,
    };
  }

  const query = `checklist/workplace?workplace=${workplaceId}&date=${date}`;
  const { ...data } = apiService.responseHandler(await apiService.get(query));
  return data;
};
//-------------------------------------------- Checklist Completed ENDS HERE ---------------------------------------------

//-------------------------------------------------- Broadcast Messages -------------------------------------------------
const useDummyBroadcastData = false;

const dummyBroadcastMessages = {
  items: [
    {
      id: 1,
      workplaceId: 1, // Changed from 123 to 1
      message: 'Reminder: Fire drill at 3:00 PM today.',
      sentBy: {
        id: 101,
        first_name: 'Alice',
        last_name: 'Mathews',
      },
      timestamp: `${getToday()} 09:30 AM`,
    },
    {
      id: 2,
      workplaceId: 1, // Changed from 123 to 1
      message: 'New hygiene SOPs uploaded to staff portal.',
      sentBy: {
        id: 102,
        first_name: 'Raj',
        last_name: 'Verma',
      },
      timestamp: `${getToday()} 04:15 PM`,
    },
  ],
  totalCount: 2,
};

export const getBroadcastMessages = async ({
  workplaceId,
  date,
}: {
  workplaceId: number;
  date?: string;
}): Promise<any> => {
  if (useDummyBroadcastData) {
    let items = dummyBroadcastMessages.items;
    if (date) {
      items = items.filter(item => item.timestamp.startsWith(date));
    }
    return {
      items,
      totalCount: items.length,
    };
  }

  // Use new API endpoint: broadcast_messages/business-day
  const dateParam = date || getToday();
  const query = `broadcast_messages/business-day?pagination=false&workplace=${workplaceId}&date=${dateParam}`;
  const { ...data } = apiService.responseHandler(await apiService.get(query));
  
  // Transform API response to match expected format
  // API returns { member: [...] } but component expects { items: [...] }
  if (data.member && Array.isArray(data.member)) {
    return {
      items: data.member,
      totalCount: data.totalItems || data.member.length,
    };
  }
  
  return {
    items: [],
    totalCount: 0,
  };
};
//-------------------------------------------- Broadcast Messages ENDS HERE ---------------------------------------------

//------------------------------------------------------ Shift Notes ----------------------------------------------------
const useDummyShiftNotesData = false;

const dummyShiftNotesData = [
  {
    id: 1,
    workplace: { id: 1, name: 'Main Branch' }, // Changed from 123 to 1
    author: {
      id: 1,
      first_name: 'John',
      middle_initial: '',
      last_name: 'Doe',
      email: 'john.doe@example.com',
    },
    date_of_the_note: getNowISO(),
    day_part: 'Morning',
    note_title: 'Freezer Issue',
    note_details: 'Freezer was defrosted early morning.',
    created_at: getNowISO(),
  },
  {
    id: 2,
    workplace: { id: 1, name: 'Main Branch' }, // Changed from 123 to 1
    author: {
      id: 2,
      first_name: 'Jane',
      middle_initial: '',
      last_name: 'Smith',
      email: 'jane.smith@example.com',
    },
    date_of_the_note: getNowISO(),
    day_part: 'Afternoon',
    note_title: 'Bread Shortage',
    note_details: 'Shortage of bread rolls reported.',
    created_at: getNowISO(),
  }
];
  //---------------------------------------------- GET Shift Notes ----------------------------------------------
  export const getShiftNotes = async ({
    workplaceId,
    date,
  }: {
    workplaceId: number;
    date?: string;
  }): Promise<any> => {
    if (useDummyShiftNotesData) {
      let items = Array.isArray(dummyShiftNotesData) ? dummyShiftNotesData : [];
  
      if (date) {
        items = items.filter(item =>
          item?.date_of_the_note?.startsWith(date)
        );
      }
  
      return items;
    }
  
    // New API endpoint format: api/shift_notes?page=1&itemsPerPage=50&date=2025-08-26&workplace=1
    const queryParams = [`page=1`, `itemsPerPage=50`, `workplace=${workplaceId}`];
    if (date) {
      queryParams.push(`date=${date}`);
    }
    const query = `shift_notes?${queryParams.join('&')}`;
    console.log('Shift Notes API Query:', query);
    const data = apiService.responseHandler(await apiService.get(query));
    console.log('Shift Notes API Response:', data);
    return data;
  };
  //---------------------------------------------- GET Shift Notes ENDS HERE----------------------------------------------
  //---------------------------------------------- POST Shift Notes ----------------------------------------------
  export const postShiftNote = async ({
    note,
    workplaceId,
    date,
  }: {
    note: string;
    workplaceId: number;
    date?: string;
  }): Promise<any> => {
    if (useDummyShiftNotesData) {
      const newNote = {
        id: dummyShiftNotesData.length + 1,
        workplace: { id: 1, name: 'Main Branch' }, // Always use static workplace ID 1
        author: {
          id: 999,
          first_name: 'User',
          middle_initial: '',
          last_name: 'Name',
          email: 'user.name@example.com',
        },
        date_of_the_note: date || new Date().toISOString(),
        day_part: 'Morning',
        note_title: 'General',
        note_details: note,
        created_at: date || new Date().toISOString(),
      };
      dummyShiftNotesData.push(newNote);
      return newNote;
    }
  
    // Remove HTML tags from the note content
    // const cleanNote = note.replace(/<[^>]*>/g, '');
    
    const payload = { 
      note, 
      workplace: `api/workplaces/${workplaceId}`, // Dynamic workplace ID
      ...(date && { date }) // Add date if provided
    };
    console.log('POST Shift Note Payload:', payload);
    const response = await apiService.post('shift_notes', payload);
    console.log('POST Shift Note Response:', response);
    return apiService.responseHandler(response);
  };

  //-------------------------------------------- POST Shift Notes ENDS HERE --------------------------------------------
  //---------------------------------------------- PUT Shift Notes ----------------------------------------------
  export const putShiftNote = async ({
    id,
    note,
    workplaceId,
    date,
  }: {
    id: number;
    note: string;
    workplaceId: number;
    date?: string;
  }): Promise<any> => {
    // Remove HTML tags from the note content
    // const cleanNote = note.replace(/<[^>]*>/g, '');
    
    const payload = { 
      note: note, 
      workplace: `api/workplaces/${workplaceId}`, // Dynamic workplace ID
      ...(date && { date }) // Add date if provided
    };
  
    if (useDummyShiftNotesData) {
      const index = dummyShiftNotesData.findIndex((n) => n.id === id);
      if (index !== -1) {
        dummyShiftNotesData[index] = {
          ...dummyShiftNotesData[index],
          note_details: note,
          date_of_the_note: date || new Date().toISOString(),
          created_at: date || new Date().toISOString(),
        };
        return dummyShiftNotesData[index];
      } else {
        throw new Error(`Shift note with id ${id} not found in dummy data`);
      }
    }
  
    const response = await apiService.put(`shift_notes/${id}`, payload);
    console.log('PUT Shift Note Response:', response);
    return apiService.responseHandler(response);
  };
  //---------------------------------------------- PUT Shift Notes ENDS HERE----------------------------------------------
  //---------------------------------------------- DELETE Shift Notes ----------------------------------------------
  export const deleteShiftNote = async (id: number): Promise<any> => {
    if (useDummyShiftNotesData) {
      const index = dummyShiftNotesData.findIndex((note) => note.id === Number(id));
      if (index !== -1) {
        const deleted = dummyShiftNotesData.splice(index, 1)[0];
        return deleted;
      } else {
        throw new Error(`Shift Note with id ${id} not found`);
      }
    }
  
    const response = await apiService.delete(`shift_notes/${id}`);
    console.log('DELETE Shift Note Response:', response);
    return apiService.responseHandler(response);
  };
  //------------------------------------------- DELETE Shift Notes ENDS HERE----------------------------------------------
//------------------------------------------------ Shift Notes ENDS HERE ------------------------------------------------

//------------------------------------------------------ Employee Notes ----------------------------------------------------

//---------------------------------------------- GET Employee Notes ----------------------------------------------
export const getEmpNotes = async ({
  workplaceId,
  date,
}: {
  workplaceId: number;
  date?: string;
}): Promise<any> => {
  // New API endpoint format: api/employee_notes?page=1&itemsPerPage=10&date=2025-08-27&workplace=1
  const queryParams = [`page=1`, `itemsPerPage=10`, `workplace=${workplaceId}`];
  if (date) {
    queryParams.push(`date=${date}`);
  }
  const query = `employee_notes?${queryParams.join('&')}`;
  console.log('Employee Notes API Query:', query);
  const data = apiService.responseHandler(await apiService.get(query));
  console.log('Employee Notes API Response:', data);
  return data;
};
//------------------------------------------ GET Employee Notes ENDS HERE -----------------------------------------
//---------------------------------------------- POST Employee Notes ----------------------------------------------
export const postEmpNote = async ({
  workplaceId,
  empName,
  note,
  timestamp,
  date,
}: {
  workplaceId: number;
  empName: string;
  note: string;
  timestamp: string;
  date?: string;
}): Promise<any> => {
  // Remove HTML tags from the note content
  // const cleanNote = note.replace(/<[^>]*>/g, '');
  
  const payload = { 
    note, 
    author: `api/users/${empName}`, // Use the actual user ID for author
    workplace: `api/workplaces/${workplaceId}`, // Dynamic workplace ID
    ...(date && { date }) // Add date if provided (use date from calendar, fallback to timestamp)
  };
  
  console.log('POST Employee Note Payload:', payload);
  const response = await apiService.post('employee_notes', payload);
  console.log('POST Employee Note Response:', response);
  return apiService.responseHandler(response);
};
//------------------------------------------ POST Employee Notes ENDS HERE  ------------------------------------------
//------------------------------------------------- PUT Employee Notes ------------------------------------------------
export const putEmpNote = async ({
  id,
  workplaceId,
  empName,
  note,
  timestamp,
  date,
}: {
  id: number;
  workplaceId: number;
  empName: string;
  note: string;
  timestamp: string;
  date?: string;
}): Promise<any> => {
  // Remove HTML tags from the note content
  // const cleanNote = note.replace(/<[^>]*>/g, '');
  
  const payload = { 
    note, 
    author: `api/users/${empName}`, // Use the actual user ID for author
    workplace: `api/workplaces/${workplaceId}`, // Dynamic workplace ID
    ...(date && { date }) // Add date if provided (use date from calendar, fallback to timestamp)
  };

  const response = await apiService.put(`employee_notes/${id}`, payload);
  console.log('PUT Employee Note Response:', response);
  return apiService.responseHandler(response);
};
//---------------------------------------------- PUT Employee Notes ENDS HERE ----------------------------------------------
//---------------------------------------------- DELETE Employee Notes ----------------------------------------------
export const deleteEmpNote = async (id: number): Promise<any> => {
  const response = await apiService.delete(`employee_notes/${id}`);
  console.log('DELETE Employee Note Response:', response);
  return apiService.responseHandler(response);
};
//---------------------------------------------- DELETE Employee Notes ENDS HERE ----------------------------------------------
//---------------------------------------------- Employee Notes ENDS HERE ----------------------------------------------

//------------------------------------------------------ Users ----------------------------------------------------
//---------------------------------------------- GET Users ----------------------------------------------
export const getUsers = async (): Promise<any> => {
  // API endpoint: api/users?page=1
  const query = `users?page=1`;
  console.log('Users API Query:', query);
  const data = apiService.responseHandler(await apiService.get(query));
  console.log('Users API Response:', data);
  return data;
};
//------------------------------------------ GET Users ENDS HERE -----------------------------------------